

import React, { memo } from 'react';
import { IMAGE, VIDEO, AUDIO, FONT, OTHER, HEIC, PDF } from '../../../common/constants/fileTypes';
import { isAssetMissing, assetHasError, getTypeFromAsset } from '../../../common/utils';
import ImagePreview from './ImagePreview';
import VideoPreview from './VideoPreview';
import AudioPreview from './AudioPreview';
import FontPreview from './FontPreview';
import PdfPreview from './PdfPreview';
import OtherPreview from './OtherPreview';
import '../styles/_asset-preview.scss';


















function hasValidAssetFile({ assetFiles, type }) {
  if (!assetFiles) {return false;}

  const { ORIGINAL, SD, STREAM } = assetFiles;

  return ORIGINAL && ORIGINAL.sourceUrl ||
  type === VIDEO && SD && SD.sourceUrl ||
  type === VIDEO && STREAM && STREAM.sourceUrl && STREAM.fragments.length;
}

function getPreviewComponentFromAsset(
asset,
{
  canPlayVideo,
  canPlayAudio,
  isEditAssetDetailsModal,
  showOriginalImageAsThumbnail
})
{
  if (!asset) {return { component: OtherPreview, type: OTHER };}

  if (assetHasError(asset)) {
    return { component: OtherPreview, type: OTHER, assetError: true };
  }

  if (isAssetMissing(asset)) {
    return { component: OtherPreview, type: OTHER, assetMissing: true };
  }

  const { loadedPercent, metaData, filename, assetFiles } = asset;
  const type = getTypeFromAsset({ metaData, filename });
  const hasLoadableFile = hasValidAssetFile({ assetFiles, type });

  if (loadedPercent !== void 0 && loadedPercent !== 100 || !hasLoadableFile) {
    return { component: OtherPreview, type };
  }

  if (type === IMAGE || type === HEIC) {
    return { component: ImagePreview, type, showOriginalImageAsThumbnail };
  }

  if (type === VIDEO) {
    return { component: VideoPreview, type, canPlayVideo, isEditAssetDetailsModal };
  }

  if (type === FONT) {
    return { component: FontPreview, type };
  }

  if (type === PDF) {
    return { component: PdfPreview, type };
  }

  if (type === AUDIO && !canPlayAudio) {
    return { component: OtherPreview, type };
  }

  if (type === AUDIO) {
    return { component: AudioPreview, type };
  }

  return { component: OtherPreview, type };
}

function getStyleMeasurement(value) {
  return typeof value === 'string' ? value : `${value}px`;
}

const AssetPreviewBase = (props) => {
  const {
    asset,
    canPlayVideo,
    canPlayAudio,
    hideFileTypeIcon,
    audioThumbnail,
    defaultAudioThumbnail,
    darkMode,
    smallFont,
    isEditAssetDetailsModal,
    autoPlay,
    showOriginalImageAsThumbnail
  } = props;

  const {
    component: Preview,
    type,
    ...additionalProps
  } = getPreviewComponentFromAsset(asset, {
    canPlayVideo,
    canPlayAudio,
    isEditAssetDetailsModal,
    showOriginalImageAsThumbnail
  });

  return (
    <Preview
      asset={asset}
      type={type}
      hideFileTypeIcon={hideFileTypeIcon}
      audioThumbnail={audioThumbnail}
      defaultAudioThumbnail={defaultAudioThumbnail}
      darkMode={darkMode}
      // Should only impact font preview
      smallFont={smallFont}
      autoPlay={autoPlay}
      {...additionalProps} />);


};

function AssetPreview({
  asset,
  canPlayVideo,
  canPlayAudio,
  width = '100%',
  height = 130,
  hideFileTypeIcon,
  audioThumbnail,
  defaultAudioThumbnail,
  darkMode,
  smallFont,
  rounded,
  isEditAssetDetailsModal,
  autoPlay,
  showOriginalImageAsThumbnail = false
}) {
  if (asset === undefined) {return null;}

  const computedHeight = getStyleMeasurement(height);
  const computedWidth = getStyleMeasurement(width);

  return (
    <div
      className="asset-preview"
      style={{
        height: computedHeight,
        minHeight: computedHeight,
        width: computedWidth,
        minWidth: computedWidth,
        ...(rounded ? { borderRadius: '4px' } : {})
      }}>

      <AssetPreviewBase
        asset={asset}
        canPlayVideo={canPlayVideo}
        canPlayAudio={canPlayAudio}
        hideFileTypeIcon={hideFileTypeIcon}
        audioThumbnail={audioThumbnail}
        defaultAudioThumbnail={defaultAudioThumbnail}
        darkMode={darkMode}
        smallFont={smallFont}
        autoPlay={autoPlay}
        isEditAssetDetailsModal={isEditAssetDetailsModal}
        showOriginalImageAsThumbnail={showOriginalImageAsThumbnail} />

    </div>);

}

export default memo(AssetPreview);